// Traductor de errores técnicos → mensajes para el usuario final, orientados a
// la acción. Antes las vistas mostraban `err.message` (a menudo el texto crudo
// del backend), que para el usuario es ruido. Este helper reconoce códigos y
// situaciones conocidas (ver 50-operacion-soporte) y devuelve una clave i18n con
// un mensaje claro que dice qué pasó y qué hacer; si no reconoce el error, cae a
// un mensaje genérico (o al propio `err.message` como último recurso).
//
// Nunca expone valores de secretos ni PII: solo mapea CÓDIGOS/estados conocidos.

import type { TFunction } from 'i18next';
import { ApiError, NetworkError } from './client.js';

/**
 * Devuelve un mensaje amigable y traducido para un error de la API/red.
 *
 * @param err  El error capturado (idealmente `ApiError`/`NetworkError`).
 * @param t    La función de traducción de react-i18next.
 * @param fallbackKey  Clave i18n a usar si no se reconoce el error y no hay
 *                     `err.message` útil (por defecto `errors.generic`).
 */
export function friendlyError(err: unknown, t: TFunction, fallbackKey = 'errors.generic'): string {
  // Error de red: sin respuesta del servidor.
  if (err instanceof NetworkError) {
    return t('errors.network');
  }

  if (err instanceof ApiError) {
    // 1) Por código de negocio conocido (lo más fiable; lo fija el backend).
    const byCode = CODE_MAP[err.code];
    if (byCode) return t(byCode);

    // 2) Por combinación de status + pistas en el mensaje (Aurora reanudando).
    if (err.status === 401) return t('errors.sessionExpired');
    if (
      err.status === 503 ||
      /resuming|databaseresuming|paused/i.test(err.message) ||
      /resuming/i.test(err.code)
    ) {
      return t('errors.resuming');
    }
    if (err.status === 501) return t('errors.unavailable');
    if (err.status === 403) return t('errors.forbidden');
    if (err.status === 429) return t('errors.rateLimited');

    // 3) Si el backend mandó un mensaje legible, úsalo; si no, genérico.
    return err.message && !/^error\s+\d+$/i.test(err.message) ? err.message : t(fallbackKey);
  }

  // Error no tipado.
  if (err instanceof Error && err.message) return err.message;
  return t(fallbackKey);
}

/**
 * Mapa de `error` (código de negocio del backend) → clave i18n. Añade aquí los
 * códigos conocidos que merecen un mensaje específico orientado a la acción.
 */
const CODE_MAP: Record<string, string> = {
  billing_not_configured: 'errors.billingUnavailable',
  plan_not_payable: 'errors.planNotPayable',
  store_locator_not_configured: 'errors.locatorUnavailable',
  unknown_user: 'errors.unknownUser',
  unauthenticated: 'errors.sessionExpired',
  unknown_route: 'errors.forbidden',
};
